<template>
  <registry-page
    title-id="warehouse-movements-title"
    title="Движения"
    description="Приход, расход и остаток по проведенным складским операциям."
  >
    <template #actions>
      <q-btn flat no-caps icon="refresh" label="Обновить" :loading="loading" @click="loadMovements" />
    </template>

    <app-data-table
      data-testid="warehouse-movements-table"
      title="Журнал движений"
      :count="visibleRows.length"
      :rows="visibleRows"
      :columns="columns"
      :pagination="pagination"
      :state="tableState"
      :state-title="stateTitle"
      :state-message="errorMessage"
      storage-key="warehouse-movements"
      row-key="id"
      wrap-cells
      @reset="resetFilters"
      @retry="loadAll"
      @update:pagination="pagination = $event"
    >
      <template #filters>
        <filter-bar
          v-model="search"
          search-label="Поиск по операции, складу или номенклатуре"
          search-test-id="warehouse-movement-search"
          :has-filters="hasFilters"
          :applied-filters="appliedFilters"
          @search="applySearch"
          @remove="removeFilter"
          @reset="resetFilters"
        >
          <template #quick>
            <q-select
              :model-value="operation"
              data-testid="warehouse-movement-operation-filter"
              dense outlined emit-value map-options label="Операция"
              :options="operationOptions"
              @update:model-value="applyOperation"
            />
            <q-select
              :model-value="warehouseId"
              data-testid="warehouse-movement-warehouse-filter"
              dense outlined clearable emit-value map-options label="Склад"
              :options="warehouseOptions"
              @update:model-value="applyWarehouse"
            />
            <q-select
              :model-value="nomenclatureId"
              data-testid="warehouse-movement-nomenclature-filter"
              dense outlined clearable emit-value map-options label="Номенклатура"
              :options="nomenclatureOptions"
              @update:model-value="applyNomenclature"
            />
            <q-input :model-value="periodStart" dense outlined type="date" label="Начало" data-testid="warehouse-movement-period-start" @update:model-value="applyPeriodStart" />
            <q-input :model-value="periodEnd" dense outlined type="date" label="Конец" data-testid="warehouse-movement-period-end" @update:model-value="applyPeriodEnd" />
          </template>
          <template #advanced>
            <div class="q-gutter-md">
              <q-select :model-value="operation" dense outlined emit-value map-options label="Операция" :options="operationOptions" @update:model-value="applyOperation" />
              <q-select :model-value="warehouseId" dense outlined clearable emit-value map-options label="Склад" :options="warehouseOptions" @update:model-value="applyWarehouse" />
              <q-select :model-value="nomenclatureId" dense outlined clearable emit-value map-options label="Номенклатура" :options="nomenclatureOptions" @update:model-value="applyNomenclature" />
              <q-input :model-value="periodStart" dense outlined type="date" label="Начало" @update:model-value="applyPeriodStart" />
              <q-input :model-value="periodEnd" dense outlined type="date" label="Конец" @update:model-value="applyPeriodEnd" />
            </div>
          </template>
        </filter-bar>
      </template>
      <template #body-cell-date="props"><q-td :props="props"><date-cell :value="props.value" /></q-td></template>
      <template #body-cell-document="props">
        <q-td :props="props">
          <q-btn
            v-if="props.row.document_id"
            flat dense no-caps color="primary"
            :label="documentLabel(props.row)"
            data-testid="warehouse-movement-document-link"
            @click.stop="openDocument(props.row)"
          />
          <span v-else>—</span>
        </q-td>
      </template>
    </app-data-table>

    <q-expansion-item v-if="diagnostics.length" class="warehouse-diagnostics" icon="info_outline" label="Диагностика движений">
      <q-list dense>
        <q-item v-for="item in diagnostics" :key="item.key"><q-item-section>{{ item.message }}</q-item-section></q-item>
      </q-list>
    </q-expansion-item>
  </registry-page>
</template>

<script>
import {
  AppDataTable,
  DateCell,
  FilterBar,
  RegistryPage
} from '../../components/ui'
import {
  createWarehouseBrowseAdapter,
  isWarehouseError,
  movementColumns,
  movementOperationOptions,
  nomenclatureOption,
  normalizeDiagnostics,
  normalizeMovementRows,
  normalizeNomenclatureRows,
  normalizeWarehouseRows,
  warehouseDocumentRoutes,
  warehouseError,
  warehouseOption
} from '../../services/warehouseBrowse'

export default {
  name: 'WarehouseMovements',
  components: { AppDataTable, DateCell, FilterBar, RegistryPage },
  data() {
    const query = this.$route.query
    return {
      adapter: null,
      rows: [],
      warehouses: [],
      nomenclature: [],
      diagnostics: [],
      loading: true,
      errorMessage: '',
      accessDenied: false,
      search: String(query.q || ''),
      operation: String(query.operation || 'all'),
      warehouseId: query.warehouse || null,
      nomenclatureId: query.item || null,
      periodStart: String(query.from || ''),
      periodEnd: String(query.to || ''),
      operationOptions: movementOperationOptions,
      columns: movementColumns,
      pagination: { page: 1, rowsPerPage: 20, rowsNumber: 0 },
      requestIds: {},
      handlers: [],
      mountedReady: false
    }
  },
  computed: {
    permissions() {
      return this.$store.state.permissions || {}
    },
    canView() {
      return this.permissions.open_stock === true || this.permissions.edit_stock === true
    },
    warehouseOptions() {
      return this.warehouses.filter((row) => row.is_active !== false).map(warehouseOption)
    },
    nomenclatureOptions() {
      return this.nomenclature.filter((row) => row.is_active !== false).map(nomenclatureOption)
    },
    visibleRows() {
      const query = this.search.trim().toLocaleLowerCase('ru')
      return this.rows.filter((row) => {
        if (this.operation !== 'all' && row.operation !== this.operation) return false
        if (!query) return true
        const warehouse = row.warehouse && row.warehouse.name ? row.warehouse.name : row.warehouse_name
        const document = row.document && row.document.display_name
        return [row.operation_label, row.code, row.name, warehouse, document]
          .some((value) => String(value || '').toLocaleLowerCase('ru').includes(query))
      })
    },
    hasFilters() {
      return Boolean(this.search || this.operation !== 'all' || this.warehouseId || this.nomenclatureId || this.periodStart || this.periodEnd)
    },
    appliedFilters() {
      const filters = []
      if (this.search) filters.push({ key: 'search', label: `Поиск: ${this.search}` })
      if (this.operation !== 'all') filters.push({ key: 'operation', label: `Операция: ${this.optionLabel(this.operationOptions, this.operation)}` })
      if (this.warehouseId) filters.push({ key: 'warehouse', label: `Склад: ${this.optionLabel(this.warehouseOptions, this.warehouseId)}` })
      if (this.nomenclatureId) filters.push({ key: 'item', label: `Номенклатура: ${this.optionLabel(this.nomenclatureOptions, this.nomenclatureId)}` })
      if (this.periodStart) filters.push({ key: 'from', label: `С: ${this.periodStart}` })
      if (this.periodEnd) filters.push({ key: 'to', label: `По: ${this.periodEnd}` })
      return filters
    },
    tableState() {
      if (!this.canView || this.accessDenied) return 'access-denied'
      if (this.errorMessage) return 'error'
      if (this.loading && this.rows.length === 0) return 'loading'
      if (this.visibleRows.length === 0) return this.hasFilters ? 'filtered-empty' : 'empty'
      return 'ready'
    },
    stateTitle() {
      if (this.tableState === 'filtered-empty') return 'По выбранным условиям движений нет'
      if (this.tableState === 'error') return 'Движения не загрузились'
      return ''
    }
  },
  watch: {
    '$route.query': {
      deep: true,
      handler(query) {
        const previousServerKey = this.serverFilterKey()
        this.search = String(query.q || '')
        this.operation = String(query.operation || 'all')
        this.warehouseId = query.warehouse || null
        this.nomenclatureId = query.item || null
        this.periodStart = String(query.from || '')
        this.periodEnd = String(query.to || '')
        if (this.mountedReady && previousServerKey !== this.serverFilterKey()) this.loadMovements()
      }
    }
  },
  mounted() {
    this.adapter = createWarehouseBrowseAdapter(this.connection)
    this.bind('onGetStockMovements', this.onMovements)
    this.bind('onListWarehouses', this.onWarehouses)
    this.bind('onListNomenclature', this.onNomenclature)
    this.bind('accessDeniedErrorReceived', this.onAccessDenied)
    const ready = () => this.loadAll()
    this.handlers.push(['connectionReady', ready])
    this.mountedReady = true
    if (this.connection.isAuthenticated) ready()
    else this.emitter.on('connectionReady', ready)
  },
  beforeUnmount() {
    this.handlers.forEach(([name, handler]) => this.emitter.off(name, handler))
  },
  methods: {
    bind(name, handler) {
      this.handlers.push([name, handler])
      this.emitter.on(name, handler)
    },
    loadAll() {
      this.errorMessage = ''
      this.accessDenied = false
      this.requestIds.warehouses = this.adapter.warehouses(false)
      this.requestIds.nomenclature = this.adapter.nomenclature(false)
      this.loadMovements()
    },
    loadMovements() {
      if (!this.adapter) return
      this.loading = true
      this.errorMessage = ''
      this.requestIds.movements = this.adapter.movements({
        warehouse_id: this.warehouseId,
        nomenclature_id: this.nomenclatureId,
        period_start: this.periodStart,
        period_end: this.periodEnd
      })
    },
    onMovements(response) {
      if (response.request_id !== this.requestIds.movements) return
      this.loading = false
      if (isWarehouseError(response)) {
        this.accessDenied = response.error === 'access_denied'
        this.errorMessage = this.accessDenied ? '' : warehouseError(response, 'Не удалось загрузить движения')
        return
      }
      this.rows = normalizeMovementRows(response)
      this.diagnostics = normalizeDiagnostics(response)
      const total = response.pagination && Number(response.pagination.total)
      this.pagination = { ...this.pagination, rowsNumber: Number.isFinite(total) ? total : this.rows.length }
    },
    onWarehouses(response) {
      if (response.request_id !== this.requestIds.warehouses) return
      if (!isWarehouseError(response)) this.warehouses = normalizeWarehouseRows(response)
    },
    onNomenclature(response) {
      if (response.request_id !== this.requestIds.nomenclature) return
      if (!isWarehouseError(response)) this.nomenclature = normalizeNomenclatureRows(response)
    },
    onAccessDenied(response) {
      if (response.request_id !== this.requestIds.movements) return
      this.loading = false
      this.accessDenied = true
      this.errorMessage = ''
    },
    serverFilterKey() {
      return [this.warehouseId, this.nomenclatureId, this.periodStart, this.periodEnd].map((value) => value || '').join('|')
    },
    pushQuery(changes) {
      const query = { ...this.$route.query, ...changes }
      Object.keys(query).forEach((key) => {
        if (query[key] === null || query[key] === undefined || query[key] === '') delete query[key]
      })
      if (JSON.stringify(query) !== JSON.stringify(this.$route.query)) this.$router.push({ query })
    },
    applySearch(value) { this.pushQuery({ q: value || null }) },
    applyOperation(value) { this.pushQuery({ operation: value === 'all' ? null : value }) },
    applyWarehouse(value) { this.pushQuery({ warehouse: value || null }) },
    applyNomenclature(value) { this.pushQuery({ item: value || null }) },
    applyPeriodStart(value) { this.pushQuery({ from: value || null }) },
    applyPeriodEnd(value) { this.pushQuery({ to: value || null }) },
    removeFilter(key) {
      if (key === 'search') this.applySearch('')
      if (key === 'operation') this.applyOperation('all')
      if (key === 'warehouse') this.applyWarehouse(null)
      if (key === 'item') this.applyNomenclature(null)
      if (key === 'from') this.applyPeriodStart('')
      if (key === 'to') this.applyPeriodEnd('')
    },
    resetFilters() { this.pushQuery({ q: null, operation: null, warehouse: null, item: null, from: null, to: null }) },
    optionLabel(options, value) {
      const option = options.find((item) => String(item.value) === String(value))
      return option ? option.label : value
    },
    documentLabel(row) {
      const document = row.document || {}
      return document.display_name || `Документ ${row.document_id}`
    },
    openDocument(row) {
      const document = row.document || {}
      const documentId = row.document_id || document.id
      if (!documentId) return
      this.$router.push({ name: warehouseDocumentRoutes[document.document_type] || 'document_info_upd', params: { id: documentId, history_id: 'last' } })
    }
  }
}
</script>

<style scoped>
.warehouse-diagnostics { max-width: 56rem; margin-top: var(--sid-space-4); }
:deep(.app-data-table) { min-width: 0; }
:deep(.filter-bar__quick) { min-width: 0; flex-wrap: wrap; }
:deep(.filter-bar__quick > *) { flex: 1 1 10rem; min-width: 10rem; max-width: 15rem; }
:deep(.q-table__middle) { max-width: 100%; overflow: auto; }
</style>
